import _ from 'lodash';

import { formatPhoneNumberForDisplay } from 'utils/phone';

import {
  appointmentDateTimeOptions,
  auditTimestampOptions,
  DASH,
  DEFAULT_STATUS_BADGE_COLOR,
  OFFSET_SUFFIX_PATTERN,
  SNAPDOCS_OFFSET_TIMESTAMP_PATTERN,
  statusBadgeColorKeywords,
} from './SnapdocsClosingSection.constants';

const getTrimmedText = value => (_.isString(value) ? value.trim() : value);

export const getDisplayValue = value => {
  const text = getTrimmedText(value);

  return _.isNil(text) || text === '' ? DASH : text;
};

export const hasDisplayValue = value => getDisplayValue(value) !== DASH;

export const getStatusBadgeColor = status => {
  const normalizedStatus = String(status).toLowerCase().replace(/[_-]+/g, ' ');

  return (
    statusBadgeColorKeywords.find(({ keywords }) =>
      keywords.some(keyword => normalizedStatus.includes(keyword)),
    )?.colorVariant ?? DEFAULT_STATUS_BADGE_COLOR
  );
};

export const formatLedgerStatus = status => (hasDisplayValue(status) ? _.startCase(status) : DASH);

export const getPersonFullName = ({ firstName, middleName, lastName, suffix } = {}) =>
  getDisplayValue(
    [firstName, middleName, lastName, suffix].map(getTrimmedText).filter(Boolean).join(' '),
  );

export const formatPhone = phone =>
  hasDisplayValue(phone) ? formatPhoneNumberForDisplay(phone) : DASH;

const toIsoTimestamp = timestamp => {
  const text = getTrimmedText(timestamp);

  if (!text) {
    return null;
  }

  const match = text.match(SNAPDOCS_OFFSET_TIMESTAMP_PATTERN);

  return match ? `${match[1]}T${match[2]}${match[3]}:${match[4]}` : text;
};

const formatInTimeZone = (date, timeZone) => {
  try {
    return new Intl.DateTimeFormat('en-US', { ...appointmentDateTimeOptions, timeZone }).format(
      date,
    );
  } catch {
    return new Intl.DateTimeFormat('en-US', appointmentDateTimeOptions).format(date);
  }
};

export const formatAppointmentDateTime = ({ appointmentAt, appointment }) => {
  const isoTimestamp = toIsoTimestamp(appointmentAt) ?? toIsoTimestamp(appointment?.timestamp);
  const date = isoTimestamp ? new Date(isoTimestamp) : null;

  if (!date || Number.isNaN(date.getTime())) {
    return getDisplayValue(appointment?.date);
  }

  return formatInTimeZone(date, getTrimmedText(appointment?.timezoneIana) || undefined);
};

export const formatAppointmentLocation = appointment => {
  if (!appointment) {
    return DASH;
  }

  const { streetAddress, city, state, zip } = appointment;
  const stateAndZip = [state, zip].map(getTrimmedText).filter(Boolean).join(' ');

  return getDisplayValue(
    [streetAddress, city, stateAndZip].map(getTrimmedText).filter(Boolean).join(', '),
  );
};

export const formatLenderReviewed = lenderReviewed => {
  if (_.isNil(lenderReviewed)) {
    return DASH;
  }

  return lenderReviewed ? 'Yes' : 'No';
};

export const humanizeAuditAction = action => {
  const words = _.toLower(_.trim(action).replace(/[._\-\s]+/g, ' ')).trim();

  return words ? _.upperFirst(words) : DASH;
};

const parseAuditTimestamp = createdAt => {
  const text = getTrimmedText(createdAt);

  if (!text) {
    return null;
  }

  // The Integrations service can emit offset-less UTC timestamps, which Date would read as local time.
  const date = new Date(OFFSET_SUFFIX_PATTERN.test(text) ? text : `${text}Z`);

  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatAuditTimestamp = createdAt => {
  const date = parseAuditTimestamp(createdAt);

  return date ? new Intl.DateTimeFormat('en-US', auditTimestampOptions).format(date) : DASH;
};

export const sortAuditEventsNewestFirst = events =>
  _.orderBy(events, event => parseAuditTimestamp(event.createdAt)?.getTime() ?? 0, 'desc');
